import { useMemo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { Tooltip } from "@fluentui/react-components";
import DealTeamAvatarGroupCell from "../../../../dashboard-util/dashboard-telerik-helper/deal-team-avatar";
import { renderTooltipHeader, createColumnMenu } from "../../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { getFlattenedData, getFilteredSortedDatDialog, highlightMatch, EnergyBankerNoDealOnFilterState } from "../energy-banker-time-log-helper";
import { FluentBadge } from "../../../../../../components/FluentUI/Fluent-Badge";
import "./search-dialog.scss";

const EnergySearchGrid = ({ data = [], searchValue }) => {
    const [gridState, setGridState] = useState({ sort: [], filter: null });
    const { sort, filter } = gridState;
    const flattenedData = useMemo(() => getFlattenedData(data), [data]);
    const processedData = useMemo(
        () => getFilteredSortedDatDialog(flattenedData, filter, sort),
        [flattenedData, filter, sort]
    );
    const columnMenu = useMemo(() => createColumnMenu(flattenedData, ""),[flattenedData]);
    const isFilteredEmpty = processedData.length === 0;
    const finalGridData = isFilteredEmpty ? [{ isEmptyRow: true }] : processedData;
    const columnIsFiltered = useCallback((field) => isFilteredColumn(field, filter), [filter]);
    const renderCellContent = (isEmptyRow, emptyStateContent, filledContent) =>
        isEmptyRow ? emptyStateContent : filledContent;
    const resetFilter = () => {
      const resetState = { sort: [], filter: null };
      setGridState(resetState);
    };

  return (
    <div className="energy-search-grid-wrapper">
      <Grid
        id="dialog-grid"
        data={finalGridData}
        sortable={true}
        sort={sort}
        filter={filter}
        onSortChange={e => setGridState(prev => ({ ...prev, sort: e.sort }))}
        onFilterChange={e => setGridState(prev => ({ ...prev, filter: e.filter }))}
        dataItemKey="ProjectId"
         
      >
        <Column
          field="ProjectId"
          title={renderTooltipHeader("#")}
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("ProjectId") ? "filtered" : ""}
          cell={props => {
            if (props.dataItem.isEmptyRow) {
            return (
                <td colSpan={5} className="text-center k-table-td">
                    <EnergyBankerNoDealOnFilterState resetDataState={resetFilter} />
                </td>
            );
            }
            return (
            <td className="background-white k-table-td">
                <FluentBadge
                    shape="rounded"
                    appearance="tint"
                    size="medium"
                    color="informative"
                    text={props.dataItem.ProjectIdString}
                />
            </td>
            );
        }}
        />
        <Column
            field="Company"
            title={renderTooltipHeader("Company")}
            columnMenu={columnMenu}
            headerClassName={columnIsFiltered("Company") ? "filtered" : ""}
            cell={props =>
            renderCellContent(
                props.dataItem.isEmptyRow,
                null,
                <td className="background-white k-table-td">
                <Tooltip content={props.dataItem.Company} relationship="label">
                    <span className="truncate-text">{highlightMatch(props.dataItem.Company, searchValue)}</span>
                </Tooltip>
                </td>
            )}
        />
        <Column
          field="DealStatus"
          title={renderTooltipHeader("Deal Status")}
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("DealStatus") ? "filtered" : ""}
          cell={props => (
            renderCellContent(
                props.dataItem.isEmptyRow,
                null,
            <td className="background-white k-table-td">
                <FluentBadge
                    shape="rounded"
                    appearance="tint"
                    size="medium"
                    color={props.dataItem.DealStatus === "Live" ? "success" : "informative"}
                    text={props.dataItem.DealStatus}
                />
            </td>
            ))}
        />
        <Column
          field="ProjectName"
          title={renderTooltipHeader("Deal Name")}
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("ProjectName") ? "filtered" : ""}
          cell={props => (
            renderCellContent(
              props.dataItem.isEmptyRow,
              null,
            <td className="background-white k-table-td">
              <Tooltip content={props.dataItem.ProjectName} relationship="label">
                <span className="truncate-text">{highlightMatch(props.dataItem.ProjectName, searchValue)}</span>
              </Tooltip>
            </td>
          ))}
        />
        <Column
          field="Members"
          title={renderTooltipHeader("Deal Team")}
          filter="text"
          columnMenu={columnMenu}
          headerClassName={columnIsFiltered("Members") ? "filtered" : ""}
          cell={props => (
            renderCellContent(
              props.dataItem.isEmptyRow,
              null,
            <td className="background-white k-table-td">
              {props.dataItem.Members?.length ? (
                <DealTeamAvatarGroupCell members={props.dataItem.Members} />
              ) : (
                "-"
              )}
            </td>
          ))}
        />
      </Grid>
    </div>
  );
};

EnergySearchGrid.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      ProjectId: PropTypes.number.isRequired,
      ProjectIdString: PropTypes.string.isRequired,
      Company: PropTypes.string.isRequired,
      DealStatus: PropTypes.string.isRequired,
      ProjectName: PropTypes.string.isRequired,
      Members: PropTypes.arrayOf(
        PropTypes.shape({ Id: PropTypes.number, Name: PropTypes.string, IsOwner: PropTypes.bool })
      )
    })
  ).isRequired,
  searchValue: PropTypes.string.isRequired
};

export default EnergySearchGrid;